import { type JSX, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { AppBackground } from "@/components/app-background";
import { MallaMark, MeshIllustration } from "@/components/auth/malla-brand";

/**
 * Malla's OWN auth shell — deliberately NOT the shared `AuthLayout` + glass
 * `AuthCard` that every clone inherited (and which therefore read as a clone).
 *
 * Malla is a mesh VPN **control plane**, so its auth is shaped like a network
 * console, not a centered glass card:
 *
 *   ┌────────────────────────────────────────────┐
 *   │  ▣ Malla           · Control plane online ·│  ← brand bar (chip + chip)
 *   │                                              │
 *   │   Sign in to your mesh.        ╲ mesh art ╱  │  ← wide hero banner: big
 *   │   Command your private network. ╲        ╱   │     headline + ambient art
 *   │ ──────────────────────────────────────────  │  ← hairline (no card border)
 *   │   Email     [ ___________________________ ]  │  ← FLAT large-touch fields
 *   │   Password  [ ___________________________ ]  │     directly on the surface
 *   │   [        Sign in        ]                  │  ← tall touch button
 *   └────────────────────────────────────────────┘
 *
 * No GlassSurface, no card chrome, no two-column brand split: a single wide
 * console with a hero band on top and the form full-bleed below, sized for
 * fingers at the counter. Structurally unlike the template's narrow editorial column
 * or the other products, even though primitives (Button/TextField/
 * SocialAuthButtons/disabled-until-ready) are shared.
 *
 * ── SCROLL CONTRACT (kit #13, same fix as melodimals #4) ───────────────────
 * `AppBackground` is `fixed inset-0 overflow-clip` and clips its children, so
 * it is used here as a pure backdrop SIBLING — never the scroll parent. The
 * content lives in its own scroll container with a FIXED height
 * (`h-[100dvh] overflow-y-auto`), not `min-h`: a min-height container grows past
 * the clipped backdrop and the form is cut off with nothing to scroll. The
 * `min-h-full` inner column keeps a short screen centred, and a tall form
 * (register) or a short landscape viewport scrolls inside this container.
 */

interface AuthKioskProps {
  /** Status chip on the brand bar (e.g. "Control plane online"). Optional. */
  status?: string;
  /** Page headline — the editorial promise for this screen. Optional for bare loading states. */
  headline?: string;
  /** Supporting line under the headline. */
  tagline?: string;
  /**
   * Heading level for the headline. The hero headline IS the page h1 for
   * Malla's auth (there is no card title). Pass "h2" only if the screen owns
   * its own h1 elsewhere.
   */
  headingLevel?: "h1" | "h2";
  /** The form (fields + actions), rendered flat below the hero banner. */
  children: JSX.Element;
  /** Footer slot (links: register / back home), rendered under the form. */
  footer?: JSX.Element;
}

export function AuthKiosk(props: AuthKioskProps) {
  const Heading = (headingProps: { children: JSX.Element }) =>
    (props.headingLevel ?? "h1") === "h2" ? (
      <h2 class="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
        {headingProps.children}
      </h2>
    ) : (
      <h1 class="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
        {headingProps.children}
      </h1>
    );

  return (
    <>
      {/* Backdrop: fixed/overflow-hidden, never the scroll parent. */}
      <AppBackground />

      {/* Real scroll container — grows past the viewport and scrolls when the
          console doesn't fit (portrait, or short landscape). */}
      <div
        class="relative z-10 h-[100dvh] w-full overflow-x-hidden overflow-y-auto px-4 sm:px-6"
        style={{
          "padding-top": "max(1.5rem, env(safe-area-inset-top))",
          "padding-bottom": "max(1.5rem, env(safe-area-inset-bottom))",
          "padding-left": "max(1rem, env(safe-area-inset-left))",
          "padding-right": "max(1rem, env(safe-area-inset-right))",
        }}
      >
        <div class="mx-auto flex min-h-full w-full max-w-lg flex-col justify-center py-6">
          {/* Brand bar — solid chip + wordmark + per-screen status chip. */}
          <div class="flex items-center justify-between gap-3">
            <Link
              to="/"
              aria-label="Malla"
              class="group flex items-center gap-2.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <span class="grid h-11 w-11 place-items-center rounded-2xl bg-accent text-accent-foreground shadow-lg shadow-accent/25 transition-transform group-hover:scale-105 group-active:scale-95">
                <MallaMark class="h-6 w-6" />
              </span>
              <span class="text-lg font-bold tracking-tight text-foreground">Malla</span>
            </Link>
            <Show when={props.status}>
              <span class="rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-accent ring-1 ring-accent/20">
                {props.status}
              </span>
            </Show>
          </div>

          {/* Hero banner — big headline + supporting line, with ambient ticket art. */}
          <Show when={props.headline}>
            <header class="relative mt-8 overflow-hidden">
              <MeshIllustration class="pointer-events-none absolute -right-6 -top-4 -z-0 hidden h-44 text-accent opacity-[0.08] sm:block" />
              <div class="relative z-10 max-w-md space-y-3">
                <Heading>{props.headline}</Heading>
                <Show when={props.tagline}>
                  <p class="text-pretty text-base leading-relaxed text-muted-foreground">
                    {props.tagline}
                  </p>
                </Show>
              </div>
            </header>
          </Show>

          {/* Hairline rule instead of a card border. */}
          <div class="my-7 h-px w-full bg-border/70" />

          {/* Form lives flat on the ambient surface (no glass card). */}
          <div class="w-full">{props.children}</div>

          <Show when={props.footer}>
            <footer class="mt-7 space-y-3 text-center">{props.footer}</footer>
          </Show>
        </div>
      </div>
    </>
  );
}
